import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Mutation } from '@tanstack/react-query';
import { TotpStepUpModal } from './TotpStepUpModal';

/**
 * Phase 14b-1-5 — TOTP step-up modal provider with FIFO retry queue.
 *
 * Wires the global TanStack Query MutationCache `onError` handler (set
 * up in `main.tsx`) into the React tree so destructive admin mutations
 * that fail with `TotpStepUpRequiredError` can:
 *
 *   1. enqueue themselves on a shared FIFO queue,
 *   2. open the step-up modal (once — concurrent failures stack on the
 *      queue but do not stack modals),
 *   3. on successful step-up, replay every queued mutation in FIFO order
 *      via the TanStack `mutation.execute(variables)` API,
 *   4. on operator cancel, drain the queue.
 *
 * Race conditions handled:
 *   - Another destructive mutation fires WHILE step-up POST is in flight
 *     → it also enqueues; queue.length += 1; modal stays open.
 *   - Step-up succeeds for an EMPTY queue (e.g., operator cancelled all
 *     pending then re-entered code) → no-op replay loop; modal closes.
 *   - A queued mutation throws on replay → its per-mutation onError
 *     fires; subsequent queued mutations still replay.
 *
 * The bridge from MutationCache (created BEFORE the provider mounts) to
 * the provider's `enqueue` callable is the `bindStepUpHandler` API +
 * `enqueueStepUpRef` ref pattern in `main.tsx` — see that file for the
 * chicken/egg explanation.
 */

interface QueuedMutation {
  // We intentionally type as `Mutation<unknown, unknown, unknown, unknown>`
  // — the queue is heterogeneous and we only need `execute(variables)`.
  mutation: Mutation<unknown, unknown, unknown, unknown>;
}

interface TotpStepUpContextValue {
  enqueue: (mutation: Mutation<unknown, unknown, unknown, unknown>) => void;
  /** Test-only: peek at queue length. Not used in production. */
  _queueLength: number;
}

const TotpStepUpContext = createContext<TotpStepUpContextValue | null>(null);

export interface TotpStepUpModalProviderProps {
  children: ReactNode;
  /**
   * Callback invoked after the provider mounts so the MutationCache's
   * global onError handler (set up in `main.tsx`) can dispatch into
   * this provider's `enqueue` API. Solves the chicken/egg of
   * "MutationCache exists at QueryClient creation; this provider exists
   * in the React tree."
   */
  onBind?: (enqueue: (mutation: Mutation<unknown, unknown, unknown, unknown>) => void) => void;
}

export function TotpStepUpModalProvider({
  children,
  onBind,
}: TotpStepUpModalProviderProps) {
  const [open, setOpen] = useState(false);
  // Queue lives in a ref so successive enqueues during the same render
  // batch don't race with setState's async update — the modal's
  // `onSuccess` handler reads queueRef.current synchronously to drain.
  const queueRef = useRef<QueuedMutation[]>([]);
  // Mirror the queue length into state so consumers + tests can observe
  // changes for re-rendering / assertions.
  const [queueLength, setQueueLength] = useState(0);

  const enqueue = useCallback(
    (mutation: Mutation<unknown, unknown, unknown, unknown>) => {
      // Deduplicate by mutationId — TanStack Query can fire onError
      // multiple times for the same mutation under certain configs
      // (retries, observer re-binding). Keep the queue unique so the
      // replay loop doesn't execute the same mutation twice.
      const already = queueRef.current.some(
        (q) => q.mutation.mutationId === mutation.mutationId,
      );
      if (!already) {
        queueRef.current = [...queueRef.current, { mutation }];
        setQueueLength(queueRef.current.length);
      }
      // Idempotent: opening when already open is a no-op.
      setOpen(true);
    },
    [],
  );

  // Bind the enqueue function out to the MutationCache shim on mount.
  // Unbind on unmount so a re-render with a fresh provider can re-bind.
  useEffect(() => {
    if (!onBind) return;
    onBind(enqueue);
    return () => {
      // Pass a no-op so the shim doesn't hold a stale reference; the
      // next provider mount will re-bind. This keeps StrictMode's
      // double-mount safe.
      onBind(() => undefined);
    };
  }, [enqueue, onBind]);

  const drainQueue = useCallback(async () => {
    // Capture + clear synchronously so concurrent enqueues during the
    // replay land on a fresh queue (will re-open the modal, which is
    // correct — those failed AFTER the step-up succeeded, so a new
    // freshness window may be needed).
    const pending = queueRef.current;
    queueRef.current = [];
    setQueueLength(0);
    setOpen(false);

    for (const { mutation } of pending) {
      try {
        // `mutation.state.variables` is the original argument set the
        // user passed to .mutate(). Replay with the same payload.
        const variables = mutation.state.variables as unknown;
        // execute(variables) returns a promise; await sequentially so
        // FIFO order is preserved across the queue per 14b-1-5.
        await mutation.execute(variables);
      } catch {
        // Per-mutation onError handlers (registered via useMutation
        // hooks at the call site) fire on the per-call lifecycle; we
        // swallow here so one failure doesn't abort the drain loop.
      }
    }
  }, []);

  const handleCancel = useCallback(() => {
    // Operator dismissed the modal — discard the queue. Each pending
    // mutation's per-call onError already fired with TotpStepUpRequiredError
    // at the point of enqueue (TanStack's MutationCache fires onError
    // BEFORE our handler runs), so call sites that registered an
    // onError can surface "action cancelled" toasts there.
    queueRef.current = [];
    setQueueLength(0);
    setOpen(false);
  }, []);

  const value = useMemo<TotpStepUpContextValue>(
    () => ({ enqueue, _queueLength: queueLength }),
    [enqueue, queueLength],
  );

  return (
    <TotpStepUpContext.Provider value={value}>
      {children}
      <TotpStepUpModal
        open={open}
        onSuccess={() => {
          void drainQueue();
        }}
        onCancel={handleCancel}
      />
    </TotpStepUpContext.Provider>
  );
}

/** Hook for consumers that need to surface the queued-mutation count
 *  (e.g., showing "3 actions pending" on the modal). v1 doesn't use
 *  this — the modal itself is generic — but it's exported for v1.x. */
export function useTotpStepUpQueueLength(): number {
  const ctx = useContext(TotpStepUpContext);
  if (!ctx) return 0;
  return ctx._queueLength;
}
